import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { initials, plural } from '../utils';
import SortableTh, { useTableSort } from '../components/SortableTh';
import useDebouncedValue from '../hooks/useDebouncedValue';

const PAGE_SIZE = 100;

export default function Employees() {
  const { withAuth } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Fetch on the settled value only — this page had no debounce at all.
  const debouncedSearch = useDebouncedValue(search);
  const [filterLocation, setFilterLocation] = useState('');
  // Defaults to active. The Workday sync now imports leavers too, so the table
  // holds every past employee — an unfiltered list is mostly people who left.
  const [filterStatus, setFilterStatus] = useState('active');
  const [locations, setLocations] = useState([]);
  const { sort, dir, toggle } = useTableSort('name');

  // Locations for the filter dropdown.
  useEffect(() => {
    withAuth(async () => {
      const res = await api.getLocations();
      setLocations((res.data || []).sort((a, b) => a.name.localeCompare(b.name)));
    });
  }, [withAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const res = await api.getEmployees({
          limit: PAGE_SIZE,
          page,
          sort,
          dir,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(filterLocation ? { locationId: filterLocation } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        });
        setEmployees(res.data || []);
        setTotal(res.total || 0);
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterLocation, filterStatus, sort, dir, page, withAuth]);

  useEffect(() => { load(); }, [load]);

  // Sorting and filtering apply across the whole result set, so a page number
  // from the previous ordering is meaningless — reset here rather than in an
  // effect, which would fetch the old page first and then refetch page 1.
  function handleSort(key) {
    toggle(key);
    setPage(1);
  }

  function handleLocationChange(value) {
    setFilterLocation(value);
    setPage(1);
  }

  function handleStatusChange(value) {
    setFilterStatus(value);
    setPage(1);
  }

  // The header used to read "N active employees" for a query that never
  // filtered by status. That was merely imprecise when the table held only
  // current staff; once leavers are imported it is off by more than half.
  const STATUS_LABEL = {
    active:     'active employees',
    terminated: 'former employees',
    '':         'employees (current and former)',
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const sortProps = { sort, dir, onSort: handleSort };

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>Employees</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>
            {total.toLocaleString()} {STATUS_LABEL[filterStatus]}
          </p>
        </div>
      </div>

      <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ width: 300 }}
        />
        <select value={filterStatus} onChange={e => handleStatusChange(e.target.value)} style={{ width: 150 }}>
          <option value="active">Active</option>
          <option value="terminated">Former</option>
          <option value="">All</option>
        </select>
        <select value={filterLocation} onChange={e => handleLocationChange(e.target.value)} style={{ width: 220 }}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {(search || filterLocation || filterStatus !== 'active') && (
          <button className="btn btn-sm" onClick={() => {
            setSearch(''); setFilterLocation(''); setFilterStatus('active'); setPage(1);
          }}>Clear</button>
        )}
      </div>

      <div style={{ padding: '20px 28px' }}>
        <div className="card">
          {loading
            ? <div className="empty-state"><div className="spinner" /></div>
            : employees.length === 0
              ? <div className="empty-state">No employees found</div>
              : (
                <table>
                  <thead>
                    <tr>
                      <SortableTh label="Employee"    sortKey="name"            {...sortProps} />
                      <SortableTh label="Employee ID" sortKey="employee_number" {...sortProps} />
                      <SortableTh label="Job Title"   sortKey="job_title"       {...sortProps} />
                      <SortableTh label="Location"  sortKey="location"  {...sortProps} />
                      <SortableTh label="Route"     sortKey="route"     {...sortProps} />
                      <SortableTh label="Assets"    sortKey="assets"    {...sortProps} />
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/employees/${emp.id}`)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              background: 'var(--accent-dim)', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: 13, fontWeight: 600, color: 'var(--accent-text)',
                            }}>
                              {initials(`${emp.first_name} ${emp.last_name}`)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                {emp.first_name} {emp.last_name}
                                {emp.employment_status === 'terminated' && (
                                  <span className="badge badge-gray">Former</span>
                                )}
                              </div>
                              {emp.email && <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{emp.email}</div>}
                            </div>
                          </div>
                        </td>
                        {/* Same fallback the detail page uses: not every record
                            carries an employee_number, but Workday's id is
                            always present. */}
                        <td>
                          <span className="mono">
                            {emp.employee_number || emp.workday_employee_id || '—'}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-1)', fontSize: 14 }}>{emp.job_title || '—'}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: 14 }}>{emp.home_location_name || '—'}</td>
                        <td><span className="mono">{emp.route_number || '—'}</span></td>
                        <td>
                          {parseInt(emp.asset_count) > 0
                            ? <span className="badge badge-blue">{emp.asset_count} {plural(emp.asset_count, 'device')}</span>
                            : <span style={{ color: 'var(--text-2)', fontSize: 13 }}>None</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          }
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
            <button className="btn btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '4px 12px', fontSize: 14, color: 'var(--text-2)' }}>
              Page {page} of {totalPages} &nbsp;·&nbsp; {total} employees
            </span>
            <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
